import { Theme } from '@mui/material';
import { GeoJSONSource, GeoJSONSourceRaw } from '@trimblemaps/trimblemaps-js';

import { Lane } from '../../../interfaces/services/lanes';
import { getLaneId } from '../../stateHelpers';
import { FeatureTypes, MapSources } from '../enums';

/** Interface used for the properties object on the ShipperDetails Lanes Map Source. */
export interface LanesMapSourceProperties {
    id: string;
    featureType: FeatureTypes;
    description: string;
    color: string;
    strokeColor?: string;
    isHovered?: boolean;
}

/**
 * Loop through the lanes and add a point feature to the source for each lane origin and destination.
 * Loop through the lanes and add a line feature to the source for each lane.
 * @param lanes The lanes to show on the map
 * @param hoveredLaneId The id of the hovered lane
 * @param theme MUI theme to use for styling
 * @returns A list of geojson features
*/
export const determineLanesSource = ({
    lanes,
    hoveredLaneId,
    theme,
}: {
    lanes: Lane[];
    hoveredLaneId: string | null;
    theme: Theme;
}): GeoJSON.Feature<GeoJSON.Point | GeoJSON.LineString, LanesMapSourceProperties>[] => {
    const features: GeoJSON.Feature<GeoJSON.Point | GeoJSON.LineString, LanesMapSourceProperties>[] = [];

    lanes.forEach((lane): void => {
        const laneId = getLaneId(lane);

        // by default the lanes are displayed as "hovered"
        let isHovered = true;
        if (hoveredLaneId) {
            if (laneId === hoveredLaneId) {
                isHovered = true;
            } else {
                isHovered = false;
            }
        }

        if (lane.pickup_service_area_longitude_value && lane.pickup_service_area_latitude_value &&
            lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
            const laneTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Origin:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.pickup_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Destination:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.delivery_service_area_name}</strong></div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [lane.pickup_service_area_longitude_value, lane.pickup_service_area_latitude_value],
                        [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                    ],
                },
                properties: {
                    id: laneId,
                    featureType: FeatureTypes.LaneLine,
                    description: laneTooltipHtml,
                    color: theme.palette.secondary.main,
                    isHovered,
                },
            });
        }

        if (lane.pickup_service_area_longitude_value && lane.pickup_service_area_latitude_value) {
            const pickupAreaTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.pickup_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${lane.pickup_service_area_id}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lane.pickup_service_area_longitude_value, lane.pickup_service_area_latitude_value],
                },
                properties: {
                    id: lane.pickup_service_area_id,
                    featureType: FeatureTypes.LaneCenter,
                    description: pickupAreaTooltipHtml,
                    color: theme.palette.secondary.main,
                    strokeColor: theme.palette.info.main,
                    isHovered,
                },
            });
        }

        if (lane.delivery_service_area_longitude_value && lane.delivery_service_area_latitude_value) {
            const deliveryAreaTooltipHtml = `
                <div class='mapTooltip'>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'><strong>Area Name:</strong></div>
                        <div class='mapTooltipCell'><strong>${lane.delivery_service_area_name}</strong></div>
                    </div>
                    <div class='mapTooltipRow'>
                        <div class='mapTooltipCell'>Area Id:</div>
                        <div class='mapTooltipCell'>${lane.delivery_service_area_id}</div>
                    </div>
                </div>
            `;

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lane.delivery_service_area_longitude_value, lane.delivery_service_area_latitude_value],
                },
                properties: {
                    id: lane.delivery_service_area_id,
                    featureType: FeatureTypes.LaneCenter,
                    description: deliveryAreaTooltipHtml,
                    color: theme.palette.secondary.main,
                    strokeColor: theme.palette.info.main,
                    isHovered,
                },
            });
        }
    });

    return features;
};

/**
 * Loop through the lanes and add a point feature to the source for each lane origin and destination.
 * Loop through the lanes and add a line feature to the source for each lane.
 * Adds the lanes source to the map if it doesn't already exist
 * @param map Instance of your map
 * @param lanes The lanes to show on the map
 * @param hoveredLaneId The id of the hovered lane
 * @param theme MUI theme to use for styling
 * @returns A geojson source to be added to the map
*/
export const addLanesSource = ({
    map,
    lanes,
    hoveredLaneId,
    theme,
}: {
    map: TrimbleMaps.Map;
    lanes: Lane[];
    hoveredLaneId: string | null;
    theme: Theme;
}): GeoJSONSourceRaw => {
    const features = determineLanesSource({
        lanes,
        hoveredLaneId,
        theme,
    });

    const source: GeoJSONSourceRaw = {
        type: 'geojson',
        data: {
            type: 'FeatureCollection',
            features,
        },
    };

    if (map.getSource(MapSources.lanesSource) === undefined) {
        map.addSource(MapSources.lanesSource, source);
    } else if (source.data) {
        (map.getSource(MapSources.lanesSource) as GeoJSONSource).setData(source.data);
    }

    return source;
};
